'use client';

import { Button, Stack, Theme } from '@mui/material';
import {
  BiometricConfidenceCard,
  DocumentConfidenceCard,
  EvidenceThumbnail,
  PermissionNotice,
  RequestLinkSlot,
  requestActionCopy,
  TBiometricConfidenceLevel,
  TDocumentConfidenceLevel,
} from '@verifime/components';
import { ReactElement, useState } from 'react';
import BiometricEvidence from './BiometricEvidence';
import IdPortraitEvidence from './IdPortraitEvidence';
import { TRequestState } from './requestState';
import { useConfidenceCards } from './useConfidenceCards';

export type TCustomerConfidenceCardsProps = {
  customerId?: string;
  /**
   * The organisation whose permission decides whether a check may be requested.
   *
   * **Re-introduced when the gate moved to the permission lookup**, having been removed while this
   * view read its answer from the task
   * list — that call needed no organisation because person-api resolved the caller's own. The
   * permission list does not: it returns every organisation's rows for the customer, and picking
   * this organisation's out of them is the caller's job, so the id has to come in.
   */
  organisationId?: string;
  /** Called when a client wants the document assessment. Omit to leave that button out entirely. */
  onViewAssessment?: () => void;
  /**
   * Called for every failed request behind this view. The views package has no logging surface, so
   * without it a failure is visible on screen and unobservable anywhere else.
   */
  onError?: (error: unknown) => void;
  'data-testid'?: string;
};

/**
 * `VIEW EVIDENCE` is filled at `Low` and outlined everywhere else, and the same for
 * `VIEW ASSESSMENT` on the document card.
 *
 * `Low` is the one outcome a client has to act on, so it is the one the design fills. Everywhere
 * else the control stays available but stops competing for attention, which is the honest way to say
 * "not the thing to act on" without hiding a control for a reason the reader cannot see.
 */
function emphasisFor(isLow: boolean): 'contained' | 'outlined' {
  return isLow ? 'contained' : 'outlined';
}

/**
 * The actions on the biometric card: view the evidence, ask again, and the request link on its own
 * row beneath.
 *
 * The request button's label and `data-action` both come from `requestActionCopy` rather than
 * literals, so the verb and the attribute a test or an analytics query selects on cannot drift
 * apart. It is offered at every level: even at `High` a client may want a fresh check, since ongoing
 * due diligence is periodic and a client who doubts the earlier capture is not chasing a bigger
 * number.
 *
 * **`onRequestFaceCheck` is `useFaceCheckTasks`'s own action, not a prop threaded down from the
 * page.** It used to be: `CustomerConfidenceCards` took an `onRequestFaceCheck` prop that no page
 * ever passed, so the button rendered enabled and did nothing (issue #2049). Unlike
 * `onViewAssessment`, which genuinely needs page-specific data (the assessment id, the organisation,
 * where to navigate), asking for a face check needs nothing beyond the customer id this component
 * already holds, so there is no reason for a caller to own it at all. Removing the seam removes the
 * defect class with it: there is no longer a wire to forget.
 *
 * **`VIEW EVIDENCE` is not offered at `NotCompleted`.** That level means the customer never did the
 * face check, so there is no face-check capture, no similarity score and no liveness determination:
 * the dialog this button opens would show none of them. `InProgress` keeps the button, because it
 * means the face check did complete and only the ID portrait failed to scan across, so there is
 * genuinely something to look at. This is the prototype's rule, which gated the control on having a
 * capture; it was lost when the prototype was rebuilt as views, and the page story is what caught it.
 *
 * **`VIEW ID PORTRAIT` is offered at `NotCompleted` instead, but only when one exists.** A face check
 * being unstarted says nothing about the identity document: its portrait can still be on file, and a
 * staff member comparing the customer face to face still needs to see it. `hasIdPortrait` is the only
 * signal the API offers for that (there is no availability field), so the button is withheld rather
 * than opening onto an empty panel whenever the fetch behind it comes back with nothing.
 */
function biometricActions(
  level: TBiometricConfidenceLevel,
  requestState: TRequestState,
  hasIdPortrait: boolean,
  mayRequest: boolean,
  onViewEvidence: () => void,
  onViewIdPortrait: () => void,
  onRequestFaceCheck: () => void,
): ReactElement | undefined {
  const { label, dataAction } = requestActionCopy(requestState.hasOutstandingRequest);
  const hasCapture = level !== 'NotCompleted';
  // `level === 'NotCompleted'` is deliberate defence in depth, not an independent condition. The
  // caller (`CustomerConfidenceCards`) only ever enables `useIdPortraitEvidence`'s fetch when
  // `isNotCompleted`, so `hasIdPortrait` can only be true when the level is already `NotCompleted`:
  // today this clause cannot fail on its own, and no test can exercise it independently of the
  // fetch gate above it. It stays because the two gates live in different places and are computed
  // separately; if a future change ever lets `hasIdPortrait` become true without the level being
  // `NotCompleted` (e.g. the fetch gate is loosened), this clause is what keeps the button honest,
  // for the cost of one comparison. Do not delete it as redundant, and do not add a test that
  // claims to cover it independently: nothing here can, until the fetch gate can produce
  // `hasIdPortrait: true` at another level.
  const showIdPortraitButton = level === 'NotCompleted' && hasIdPortrait;

  // Nothing at all to render: the caller may not ask, and this level offers nothing else. The card
  // omits its action row rather than drawing an empty one, and `PermissionNotice` takes the space
  // the control had. Returning `undefined` rather than an empty fragment is what makes that happen.
  // `hasEvidenceAction` rather than the same test written out a second time: it is the predicate
  // the card's notice reads as well, and its own docblock is about why the two must not be computed
  // apart.
  if (!mayRequest && !hasEvidenceAction(level, hasIdPortrait)) {
    return undefined;
  }

  return (
    <Stack sx={(t: Theme) => ({ gap: t.semantic.spacing.xs })}>
      {/* `flexWrap: 'wrap'` rather than a breakpoint (ruling 6, A12): the prototype's own
          `ConfidenceChecks.tsx` uses the same mechanism on this row and has no viewport logic
          anywhere. Without it, a card narrow enough to squeeze both buttons wraps each button's
          own label instead, which grows the button from its single-line ~40px to about 68px. With
          it, the row drops `REQUEST AGAIN`/`REQUEST NOW` to its own line at 40px instead. Each
          button's `whiteSpace: 'nowrap'` is what makes that the row's choice rather than the
          label's: without it, a button that still has room to shrink wraps its own text before the
          row gives up and wraps the button. */}
      <Stack direction="row" flexWrap="wrap" sx={(t: Theme) => ({ gap: t.semantic.spacing.xs })}>
        {hasCapture && (
          <Button
            variant={emphasisFor(level === 'Low')}
            data-action="view-biometric-evidence"
            onClick={onViewEvidence}
            sx={{ whiteSpace: 'nowrap' }}
          >
            VIEW EVIDENCE
          </Button>
        )}
        {showIdPortraitButton && (
          <Button
            variant="outlined"
            data-action="view-id-portrait"
            onClick={onViewIdPortrait}
            sx={{ whiteSpace: 'nowrap' }}
          >
            VIEW ID PORTRAIT
          </Button>
        )}
        {mayRequest && (
          <Button
            variant="outlined"
            data-action={dataAction}
            onClick={onRequestFaceCheck}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {label}
          </Button>
        )}
      </Stack>
      {/* Goes with the control it belongs to. Its line — `No outstanding requests` — is a statement
          about a request history, and a caller who may not ask has nothing it could usefully say.

          **Reachable now, unlike under the previous gate.** That gate read the task list itself, so
          a refused caller held no task to link to and this branch could never be taken with a live
          link behind it. The gate reads the permission list instead, and a permission can be revoked
          after a task was created — leaving an outstanding request, a tracking url, and no live
          permission. Whether that caller should still be able to copy the link is an open question
          for the ticket owner; today it is withheld with the rest, and
          `DeniedWithAnOutstandingRequest` pins that so a change to it shows up as a red story. */}
      {mayRequest && <RequestLinkSlot url={requestState.requestUrl} />}
    </Stack>
  );
}

/**
 * Whether the card has something to offer besides asking for a check.
 *
 * One named predicate rather than the condition written twice, because it decides two things that
 * have to agree: which controls survive a refusal, and whether the card explains itself. Computed
 * apart, they drift, and the drift is silent — a card that explains itself while still offering a
 * control, or one that offers nothing and says nothing.
 *
 * `VIEW ID PORTRAIT` counts, the same as `VIEW EVIDENCE`. The two are mutually exclusive by level
 * and they are treated alike here deliberately: from the reader's side both are "one fewer button
 * than the last customer had", and the explanation is reserved for the state where the row empties.
 */
function hasEvidenceAction(level: TBiometricConfidenceLevel, hasIdPortrait: boolean): boolean {
  return level !== 'NotCompleted' || hasIdPortrait;
}

/**
 * Folds this axis's four states down to the card's three, for `DocumentConfidenceCard` and
 * `BiometricConfidenceCard`'s own `status` prop.
 *
 * **`unavailable` renders as `error`.** Until this task an `unavailable` axis drew no card at all,
 * on the reasoning that the hook already distinguishes a failed call from one that succeeded
 * carrying nothing, and only the first deserved a card saying so. In practice that left a client
 * looking at a bare "Last updated on" caption with no date and no explanation, which review judged
 * the worse of the two failure modes. Charles ruled in verifime-adr#264 that reusing the existing
 * failure treatment (heading, warning icon, `UNAVAILABLE_LEVEL_NAME` and `UNAVAILABLE_STATEMENT`) is
 * preferable, even though "could not be loaded" is not literally true when nothing failed.
 *
 * **The distinction is not deleted, only folded at this last step.** `useCustomerConfidence` still
 * tells `unavailable` apart from `error` in the data it hands back (see its own docblock), so a
 * future caller that needs the difference still has it. This function is where the two converge on
 * one rendering, not where the difference is decided.
 */
function cardStatus(status: 'loading' | 'error' | 'unavailable'): 'loading' | 'error' {
  return status === 'loading' ? 'loading' : 'error';
}

/**
 * The customer's two confidence check cards, and the evidence dialog behind the biometric one.
 *
 * **Every axis renders a card now, including `unavailable`.** See `cardStatus` for why: a blank
 * card was a worse outcome than reusing the existing failure treatment, even for a state where
 * nothing actually failed.
 *
 * **The two axes fail together**, because they come from one request. There is no state in which the
 * document card errored and the biometric card did not.
 */
export default function CustomerConfidenceCards({
  customerId,
  organisationId,
  onViewAssessment,
  onError,
  'data-testid': dataTestId,
}: Readonly<TCustomerConfidenceCardsProps>): ReactElement {
  // Every derivation this card renders from, in one call. What it does not hand back is anything
  // that builds an element: the shared dialog button below stays here, in the view that renders it.
  const {
    documentAxis,
    biometricAxis,
    requestState,
    requestFaceCheck,
    isResolving,
    mayRequest,
    requestPermission,
    isNotCompleted,
    hasIdPortrait,
    portraitUrl,
    mediaSrc,
  } = useConfidenceCards({ customerId, organisationId, onError });

  // Built once and handed to both dialogs. It was written out at each of them, comment included,
  // which put the request control in three places in this one file and its gate in two. A change to
  // the control's shape then had three landing sites and one explanation maintained in duplicate.
  //
  // Gated on the same signal as the card's own control: left ungated, a dialog reached through
  // `VIEW EVIDENCE` was a second door onto the action the card had just withheld.
  const dialogRequestAction = mayRequest ? (
    <Button
      variant="outlined"
      data-action={requestActionCopy(requestState.hasOutstandingRequest).dataAction}
      onClick={requestFaceCheck}
    >
      {requestActionCopy(requestState.hasOutstandingRequest).label}
    </Button>
  ) : undefined;
  const [isEvidenceOpen, setIsEvidenceOpen] = useState(false);
  const [isIdPortraitOpen, setIsIdPortraitOpen] = useState(false);

  return (
    // `alignItems="stretch"` is the whole reason the pair reads as one row: it makes the shorter
    // card take the taller one's height, and the card layout then pins both timestamps to the foot
    // of that height. Left at `flex-start` the two cards are different heights and their timestamps
    // sit at different offsets, which is what the prototype comparison caught.
    //
    // No `flexWrap`. With the cards taking `flex: 1` there is nothing to wrap: they share whatever
    // width the row has and shrink together. Wrapping let them size to their own content instead,
    // so they stacked vertically at every width and were never the same size as each other.
    <Stack
      direction="row"
      alignItems="stretch"
      data-testid={dataTestId}
      sx={(t: Theme) => ({ gap: t.semantic.spacing.xl })}
    >
      <DocumentConfidenceCard
        {...(documentAxis.status === 'ready'
          ? {
              level: documentAxis.level as TDocumentConfidenceLevel,
              lastUpdatedOn: documentAxis.lastUpdatedOn,
              actions: onViewAssessment && (
                // `flexWrap: 'wrap'` for the same reason `biometricActions` carries it (A12):
                // this card only ever has the one button today, so nothing wraps onto a second
                // line yet, but it keeps the row using the prototype's mechanism rather than none
                // at all if a second button joins it later. `whiteSpace: 'nowrap'` on the button
                // itself is what actually matters here: without it, a narrow card wraps
                // "VIEW ASSESSMENT" inside the button instead of leaving it full width.
                <Stack direction="row" flexWrap="wrap">
                  <Button
                    variant={emphasisFor(documentAxis.level === 'Low')}
                    data-action="view-document-assessment"
                    onClick={onViewAssessment}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    VIEW ASSESSMENT
                  </Button>
                </Stack>
              ),
            }
          : { status: cardStatus(documentAxis.status) })}
      />

      <BiometricConfidenceCard
        // A card carrying a capture needs more room than one that does not, or its statement
        // wraps to a word a line while the document card sits half empty. 1.3 rather than a
        // rounder number: past that the document card starts wrapping its own statement instead.
        // The prototype's own ratio (`ConfidenceChecks.tsx`), reused verbatim rather than
        // re-derived. `mediaSrc` is undefined whenever the axis is not `ready`, so this has no
        // effect on the loading or failure treatments, which both take the even split.
        sx={{ flex: mediaSrc ? 1.3 : 1 }}
        {...(biometricAxis.status === 'ready'
          ? {
              level: biometricAxis.level as TBiometricConfidenceLevel,
              lastUpdatedOn: biometricAxis.lastUpdatedOn,
              actions: biometricActions(
                biometricAxis.level as TBiometricConfidenceLevel,
                requestState,
                hasIdPortrait,
                mayRequest,
                () => setIsEvidenceOpen(true),
                () => setIsIdPortraitOpen(true),
                requestFaceCheck,
              ),
              // Explained only where withholding the control empties the row, and only on a
              // refusal. On any other failure the card says nothing: "this check could not be
              // loaded" would be false, since the confidence call is why there is a level to show,
              // and naming a permission we have not been told about would be a claim about a
              // client's relationship with their customer.
              // Say something only when the card has nothing left to offer. With `VIEW EVIDENCE` or
              // `VIEW ID PORTRAIT` still on it there is work to do here, and a sentence explaining a
              // control the reader may not have noticed was missing is noise. Unchanged by the move
              // to the permission lookup —
              // what changed is that both withheld states now speak, not just the refused one.
              //
              // Nothing is said while the lookup is still in flight: a notice that appears and is
              // replaced a moment later reads as a fault of the page.
              notice:
                !mayRequest &&
                !isResolving &&
                !hasEvidenceAction(
                  biometricAxis.level as TBiometricConfidenceLevel,
                  hasIdPortrait,
                ) ? (
                  <PermissionNotice
                    reason={requestPermission === 'notPermitted' ? 'notPermitted' : 'unknown'}
                    data-testid={dataTestId ? `${dataTestId}-permission-notice` : undefined}
                  />
                ) : undefined,
              // Supplied at every level now, so the media column no longer changes the card's
              // shape between levels: `EvidenceThumbnail` shows its own "No capture yet"
              // placeholder when there is no `src`, rather than the card omitting the slot
              // entirely. That covers `NotCompleted` and `InProgress` (no capture is fetched
              // for either, see `LEVELS_WITH_CAPTURE_TO_FETCH`) and also the brief window while
              // a `High`/`Low` fetch is still in flight: the dashed placeholder reads as "not
              // here yet", not as a failed capture, so there is no grey square to avoid holding
              // the way an earlier version of this card had to.
              media: (
                <EvidenceThumbnail
                  src={mediaSrc}
                  alt="Biometric capture"
                  // The face capture is a still from video, so it is presented on black for the
                  // same reason the dialog's face-check stage is.
                  onDark
                  onError={() => onError?.(new Error('Biometric capture thumbnail failed to load'))}
                />
              ),
            }
          : { status: cardStatus(biometricAxis.status) })}
      />

      {/* Mounted only once there is a level, because the dialog derives its summary sentence and its
          outcome reason from one. It gathers nothing until `open`. */}
      {biometricAxis.status === 'ready' && (
        <BiometricEvidence
          customerId={customerId}
          open={isEvidenceOpen}
          onClose={() => setIsEvidenceOpen(false)}
          level={biometricAxis.level as TBiometricConfidenceLevel}
          requestState={requestState}
          onError={onError}
          actions={dialogRequestAction}
        />
      )}

      {/* Mounted only once a portrait actually came back, matching the button it opens from: a
          customer change while this dialog is still open is the one path that could otherwise leave
          it mounted with nothing to show. */}
      {isNotCompleted && hasIdPortrait && (
        <IdPortraitEvidence
          open={isIdPortraitOpen}
          onClose={() => setIsIdPortraitOpen(false)}
          portraitUrl={portraitUrl}
          requestState={requestState}
          onError={onError}
          actions={dialogRequestAction}
        />
      )}
    </Stack>
  );
}
